import {
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
} from '@mui/material';
import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import * as types from 'application/_types/types.type';
import { GamifyIcon } from 'application/components/GamifyIcon';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd';
import FieldCard from './styledComponents/FieldCard';
import FlexFieldSettingsForm from './configurationForms/FlexFieldSettingsForm';

import {
  seedLocalKeys,
  createNewField,
  fieldKey,
  toCamel,
  reorder,
  stripPriv,
} from './utils';
import useOrgStore from 'application/store/zustand/useOrgStore';
import { updateStageCategory } from 'graphql/mutations';
import { API, graphqlOperation } from 'aws-amplify';
import { GamifyToast } from 'application/common/CustomToasts';
import ConfirmationDialog from 'application/components/ConfirmationDialog';

interface FormConfigProps {
  selectedCRM: types.StageCategoryPartial | null;
}

export default function FormConfig({ selectedCRM }: FormConfigProps) {
  const [fields, setFields] = useState<any[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [hasUnsaved, setHasUnsaved] = useState(false);
  // ordering mode
  const [isOrdering, setIsOrdering] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const orderSnapshotRef = useRef<any[] | null>(null); // original fields before ordering

  const categories = useOrgStore((s) => s.categories);
  const setCategories = useOrgStore((s) => s.setCategories);
  // const hasUnsaved = useMemo(
  //   () => fields.some((f) => !f.name || f.name.trim() === ''),
  //   [fields]
  // );
  // Seed local fields when CRM changes (by id) — avoids jumpiness on store writes
  useEffect(() => {
    if (!selectedCRM?.id) return;
    const serverFields =
      selectedCRM?.flexAttributes?.config?.deal?.flexFields ?? [];
    setFields(seedLocalKeys(serverFields));
    // don't reset selection here
    // exit ordering mode if CRM switched
    setIsOrdering(false);
    orderSnapshotRef.current = null;
  }, [selectedCRM?.id]);

  const selectedField = useMemo(
    () => fields.find((f) => fieldKey(f) === selectedKey) ?? null,
    [selectedKey]
  );

  // ---------- Ordering mode handlers ----------
  const onDragEnd = useCallback(
    (result: DropResult) => {
      const { destination, source } = result;
      if (!destination || destination.index === source.index) return;

      // Enter ordering mode on first reorder and snapshot original
      if (!isOrdering) {
        orderSnapshotRef.current = fields;
        setIsOrdering(true);
      }

      // Update local order only (no DB writes yet)
      setFields((prev) => reorder(prev, source.index, destination.index));
    },
    [fields, isOrdering]
  );

  const handleCancelOrder = useCallback(() => {
    if (orderSnapshotRef.current) {
      setFields(orderSnapshotRef.current);
    }
    setIsOrdering(false);
    setIsSavingOrder(false);
    orderSnapshotRef.current = null;
  }, []);

  const handleSaveOrder = useCallback(async () => {
    setIsSavingOrder(true);

    const flexAttrs = selectedCRM?.flexAttributes ?? {};
    const cfg = flexAttrs.config ?? {};
    const deal = cfg.deal ?? {};

    const nextFlexAttrs = {
      ...flexAttrs,
      config: {
        ...cfg,
        deal: { ...deal, flexFields: stripPriv(fields) },
      },
    };

    // Optimistic store update only when saving (not during drag)
    const prevCategories = categories;
    const nextCategories = categories.map((c: any) =>
      c.id === selectedCRM?.id ? { ...c, flexAttributes: nextFlexAttrs } : c
    );
    setCategories(nextCategories);

    try {
      await API.graphql(
        graphqlOperation(updateStageCategory, {
          input: {
            id: selectedCRM?.id,
            flexAttributes: JSON.stringify(nextFlexAttrs),
          },
        })
      );
      GamifyToast.success('Order saved');
      setIsOrdering(false);
      orderSnapshotRef.current = null;
    } catch (e) {
      console.error('Save order failed:', e);
      setCategories(prevCategories); // rollback store
      GamifyToast.error('Could not save new order');
      // keep ordering mode so user can try again or cancel
    } finally {
      setIsSavingOrder(false);
    }
  }, [fields, categories, selectedCRM, setCategories]);

  // ---------- Add / Submit (unchanged except it uses local fields) ----------
  const handleAddField = useCallback(() => {
    // avoid allowing add while ordering (optional)
    if (isOrdering) return;
    setHasUnsaved(true);
    const newField = createNewField();
    setFields((prev) => [newField, ...prev]);
    setSelectedKey(fieldKey(newField));
  }, [isOrdering]);

  const handleSubmit = useCallback(
    async (values: any) => {
      const localKey = values._localKey;
      const { _localKey, ...cleanField } = values;
      console.log('_localKey', _localKey);
      let nextFieldsLocal: any[];

      if (cleanField.name && cleanField.name.trim()) {
        const idx = fields.findIndex((f) => f.name === cleanField.name);
        if (idx < 0) {
          console.error('Field not found while updating');
          return;
        }
        const merged = { ...fields[idx], ...cleanField };
        nextFieldsLocal = [...fields];
        nextFieldsLocal[idx] = merged;
      } else {
        console.log('label', cleanField.label);
        const baseName = toCamel(cleanField.label || 'field');
        console.log('baseName', baseName);
        const taken = new Set(fields.map((f) => f.name));
        const uniqueName = taken.has(baseName)
          ? `${baseName}_${fields.length + 1}`
          : baseName;

        const newField = {
          ...cleanField,
          name: uniqueName,
          requiredDispositions: [],
          displayField: true,
          pickerDisplayField: false,
          hideLabel: false,
        };
        nextFieldsLocal = [newField, ...fields.slice(1)];
        console.log('newField', newField);
      }

      // Local update
      setFields(nextFieldsLocal);

      // Persist stageCategory
      const flexAttrs = selectedCRM?.flexAttributes ?? {};
      const cfg = flexAttrs.config ?? {};
      const deal = cfg.deal ?? {};
      const nextFlexAttrs = {
        ...flexAttrs,
        config: {
          ...cfg,
          deal: { ...deal, flexFields: stripPriv(nextFieldsLocal) },
        },
      };

      const prevCategories = categories;
      const nextCategories = categories.map((c: any) =>
        c.id === selectedCRM?.id ? { ...c, flexAttributes: nextFlexAttrs } : c
      );
      setCategories(nextCategories);
      setHasUnsaved(false);
      try {
        await API.graphql(
          graphqlOperation(updateStageCategory, {
            input: {
              id: selectedCRM?.id,
              flexAttributes: JSON.stringify(nextFlexAttrs),
            },
          })
        );
        GamifyToast.success('Form field saved');
        setSelectedKey(localKey);
      } catch (e) {
        console.error('Error updating stage category:', e);
        setCategories(prevCategories); // rollback store
        GamifyToast.error('Error updating stage category');
      }
    },
    [fields, categories, selectedCRM, setCategories]
  );

  const handleDeleteField = () => {
    if (!selectedField?.name || selectedField?.name.trim() === '') {
      // new unsaved field → just remove locally
      setFields((prev) =>
        prev.filter((f) => fieldKey(f) !== fieldKey(selectedField))
      );
      setHasUnsaved(false);
      if (selectedKey === fieldKey(selectedField)) setSelectedKey(null);
    } else {
      setConfirmationDialogOpen(true);
      // handleDelete();
      // saved field → log for now (later you can add real delete logic)
      // console.log('Delete requested for existing field:', selectedField);
    }
  };

  const handleDelete = useCallback(async () => {
    if (!selectedField?.name) return;

    // Local remove
    const nextFieldsLocal = fields.filter((f) => f.name !== selectedField.name);

    // Prepare flexAttributes update
    const flexAttrs = selectedCRM?.flexAttributes ?? {};
    const cfg = flexAttrs.config ?? {};
    const deal = cfg.deal ?? {};
    const nextFlexAttrs = {
      ...flexAttrs,
      config: {
        ...cfg,
        deal: { ...deal, flexFields: stripPriv(nextFieldsLocal) },
      },
    };

    const prevCategories = categories;
    const nextCategories = categories.map((c: any) =>
      c.id === selectedCRM?.id ? { ...c, flexAttributes: nextFlexAttrs } : c
    );
    setCategories(nextCategories);
    setFields(nextFieldsLocal);
    setSelectedKey(null);

    try {
      await API.graphql(
        graphqlOperation(updateStageCategory, {
          input: {
            id: selectedCRM?.id,
            flexAttributes: JSON.stringify(nextFlexAttrs),
          },
        })
      );
      GamifyToast.success('Field deleted successfully');
    } catch (e) {
      console.error('Error deleting field:', e);
      setCategories(prevCategories); // rollback store
      GamifyToast.error('Error deleting field');
    }
  }, [fields, categories, selectedCRM, selectedField, setCategories]);
  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 2,
        backgroundColor: theme.custom?.background1,
      })}
    >
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography
          sx={(theme) => ({
            fontWeight: 780,
            fontSize: 18,
            color: theme.custom?.text,
          })}
        >
          Form Settings
        </Typography>
        <Typography
          sx={(theme) => ({
            fontWeight: 400,
            fontSize: 15,
            color: theme.custom?.textLight,
          })}
        >
          Configure and customize your forms
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        {/* Left: list */}
        <Box
          sx={(theme) => ({
            background: theme.custom?.isDark
              ? 'rgba(53, 130, 158, 0.15)'
              : '#F7F7F7',
            border: `2px dashed ${theme.custom?.secondary}`,
            borderRadius: '14px',
            p: '16px 20px 24px 20px',
            width: '40%',
            minWidth: 280,
            ...(isOrdering && {
              boxShadow: '0 0 0 0 rgba(53,130,158,0.4)',
              animation: 'orderPulse 1.4s ease-in-out infinite',
              '@keyframes orderPulse': {
                '0%': { boxShadow: '0 0 0 0 rgba(53,130,158,0.35)' },
                '70%': { boxShadow: '0 0 0 8px rgba(53,130,158,0.08)' },
                '100%': { boxShadow: '0 0 0 0 rgba(53,130,158,0.00)' },
              },
            }),
          })}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: '16px',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={(theme) => ({
                fontSize: 18,
                fontWeight: 700,
                color: theme.custom?.text,
              })}
            >
              {`Form Fields (${fields.length})`}
            </Typography>

            {/* Right side actions: Add OR Save/Cancel */}
            {isOrdering ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancelOrder}
                  disabled={isSavingOrder}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveOrder}
                  disabled={isSavingOrder}
                  sx={(theme) => ({
                    backgroundColor: theme.custom?.text,
                    color: theme.custom?.background1,
                    '&:hover': {
                      backgroundColor: theme.custom?.text,
                      opacity: 0.9,
                    },
                  })}
                  startIcon={
                    isSavingOrder ? (
                      <CircularProgress
                        size={18}
                        sx={(theme) => ({ color: theme.custom?.background1 })}
                      />
                    ) : (
                      <GamifyIcon icon="stack" color="inherit" />
                    )
                  }
                >
                  {isSavingOrder ? 'Saving…' : 'Save order'}
                </Button>
              </Box>
            ) : (
              <Button
                variant="contained"
                sx={(theme) => ({
                  backgroundColor: theme.custom?.text,
                  color: theme.custom?.background1,
                  '&:hover': {
                    backgroundColor: theme.custom?.text,
                    opacity: 0.9,
                  },
                })}
                onClick={handleAddField}
                startIcon={
                  <GamifyIcon
                    icon="add"
                    backgroundColor="#03252E"
                    color="white"
                  />
                }
              >
                Add Field
              </Button>
            )}
          </Box>
          {hasUnsaved && (
            <Typography
              sx={(theme) => ({
                fontSize: 12,
                fontWeight: 400,
                color: theme.custom?.textLight,
                mb: 1,
              })}
            >
              {'Reordering is disabled while working on new fields.'}
            </Typography>
          )}
          <DragDropContext onDragEnd={hasUnsaved ? () => {} : onDragEnd}>
            <Droppable droppableId="form-fields" isDropDisabled={isSavingOrder}>
              {(droppableProvided) => (
                <Box
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                  sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                >
                  {fields.map((f: any, index: number) => {
                    const dndId = fieldKey(f);
                    // const isNew = !f.name || f.name.trim() === '';
                    return (
                      <Draggable
                        key={dndId}
                        draggableId={dndId}
                        index={index}
                        isDragDisabled={isSavingOrder || hasUnsaved}
                      >
                        {(dragProvided, dragSnap) => (
                          <Box
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            onClick={() => setSelectedKey(fieldKey(f))}
                            sx={{
                              cursor: dragSnap.isDragging ? 'grabbing' : 'grab',
                            }}
                          >
                            <FieldCard
                              field={f}
                              isSelected={selectedKey === dndId}
                            />
                          </Box>
                        )}
                      </Draggable>
                    );
                  })}
                  {droppableProvided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </Box>

        {/* Right: editor */}
        <Box sx={{ width: '60%' }}>
          {selectedField ? (
            <FlexFieldSettingsForm
              key={fieldKey(selectedField)}
              field={selectedField}
              // selectedCRM={selectedCRM as types.StageCategoryPartial}
              onSubmit={handleSubmit}
              onDelete={handleDeleteField}
            />
          ) : (
            <Box
              sx={(theme) => ({
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                color: theme.custom?.textLight,
                border: `1px solid ${theme.custom?.stroke}`,
                borderRadius: '14px',
                p: '16px 20px 24px 20px',
              })}
            >
              <Typography
                sx={(theme) => ({
                  fontSize: 18,
                  fontWeight: 700,
                  color: theme.custom?.text,
                })}
              >
                Select a field to edit
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirm={handleDelete}
        title={`Are you sure you want to delete the ${selectedField?.label} field? This action cannot be undone.`}
      />
    </Paper>
  );
}
